import { useState } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { TextButton } from "@/accounts-payable/common";
import { type ApPoLineItem } from "@/accounts-payable/types";
import { formatDollars } from "@/accounts-payable/utils";
import { RecordField } from "./record-fields";
/** How many line items show before "Show all". */
const LINE_ITEM_PREVIEW = 3;

/**
 * Line items under a "Line items (N)" label, across the card's full width:
 * description over its SKU, unit cost flush right, a divider between items.
 * A line carrying an item the run is creating gets a "New item" badge.
 * Long lists show the first few behind "Show all".
 */
export const LineItemList = ({ items }: { items: ApPoLineItem[] }) => {
  const [showAll, setShowAll] = useState(false);
  const overflow = items.length > LINE_ITEM_PREVIEW;
  const visible =
    showAll || !overflow ? items : items.slice(0, LINE_ITEM_PREVIEW);

  return (
    <RecordField label={`Line items (${items.length})`} fullWidth>
      <Stack gap="sm" fullWidth>
        <Stack
          gap="none"
          fullWidth
          className="divide-y divide-stroke-secondary"
        >
          {visible.map((it, i) => (
            <Stack
              key={i}
              direction="row"
              justify="between"
              gap="md"
              align="start"
              fullWidth
              className="py-2 first:pt-0 last:pb-0"
            >
              <Stack gap="none" className="min-w-0">
                <Typography variant="bodyW2">
                  {it.quantity} × {it.description}
                </Typography>
                <Stack direction="row" gap="xs" align="center">
                  <Typography variant="captionW1" textColor="tertiary">
                    {it.sku}
                  </Typography>
                  {it.new_pricebook_item_code && (
                    <Badge variant="blue" size="sm">
                      New item
                    </Badge>
                  )}
                </Stack>
              </Stack>
              <Typography variant="bodyW2" className="flex-none tabular-nums">
                {formatDollars(it.unit_cost)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {overflow && (
          <TextButton
            aria-expanded={showAll}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "Show fewer" : `Show all ${items.length}`}
          </TextButton>
        )}
      </Stack>
    </RecordField>
  );
};
